/** Share-link encode/decode: v2 short keys + omitted defaults; v1 legacy long keys. */

export const SHARE_FORMAT_V2 = 2;

export const DEFAULT_POT_ACCESS_AGE = 55;
export const CASH_BUFFER_DEFAULT_SPREAD_YEARS = 10;
export const CASH_BUFFER_MODE_CUSTOM = 'custom';

/** Initial calculator values (long keys) — used to omit unchanged fields in v2 links. */
export const SHARE_DEFAULTS = {
  currentAge: 40,
  retirementAge: 65,
  currentPension: 50000,
  pensionContribution: 15000,
  withdrawalAmount: 0,
  realGrowthRate: 1,
  inflationRate: 3,
  useSimulatedInflation: false,
  inflationSimSeed: 42,
  userStatePensionAnnual: 12548,
  includeStatePensionInWithdrawal: true,
  includePartnerStatePension: false,
  partnerStatePensionAnnual: 12548,
  taxFreeOption: 'phased',
  inflateWithdrawals: true,
  personalAllowance: 12570,
  basicRateThreshold: 50270,
  higherRateThreshold: 125140,
  taxFreeLumpSumCap: 268275,
  fullStatePension: 12548,
  statePensionAge: 68,
  partnerStatePensionAge: 68,
  lifeExpectancy: 90,
  potAccessAge: DEFAULT_POT_ACCESS_AGE,
  annuityEnabled: false,
  annuityPurchasePercent: 25,
  annuityPurchaseAmountExact: null,
  annuityIncomeRatePercent: 5,
  annuityAnnualGrossExact: null,
  dbPensionEnabled: false,
  dbPensionAnnual: 15000,
  dbPensionAge: 65,
  cashBufferEnabled: false,
  cashBufferMode: CASH_BUFFER_MODE_CUSTOM,
  cashBufferAmount: 0,
  cashBufferSpreadStartAge: null,
  cashBufferSpreadEndAge: null,
};

const LONG_TO_SHORT = {
  currentAge: 'a',
  retirementAge: 'r',
  currentPension: 'p',
  pensionContribution: 'c',
  withdrawalAmount: 'w',
  realGrowthRate: 'g',
  inflationRate: 'i',
  useSimulatedInflation: 'd',
  inflationSimSeed: 'h',
  userStatePensionAnnual: 'Y',
  includeStatePensionInWithdrawal: 'n',
  includePartnerStatePension: 'k',
  partnerStatePensionAnnual: 'U',
  taxFreeOption: 't',
  inflateWithdrawals: 'f',
  personalAllowance: 'A',
  basicRateThreshold: 'B',
  higherRateThreshold: 'H',
  taxFreeLumpSumCap: 'L',
  fullStatePension: 'F',
  statePensionAge: 's',
  partnerStatePensionAge: 'V',
  lifeExpectancy: 'e',
  potAccessAge: 'o',
  annuityEnabled: 'E',
  annuityPurchasePercent: 'P',
  annuityPurchaseAmountExact: 'Q',
  annuityIncomeRatePercent: 'I',
  annuityAnnualGrossExact: 'G',
  dbPensionEnabled: 'D',
  dbPensionAnnual: 'R',
  dbPensionAge: 'q',
  cashBufferEnabled: 'C',
  cashBufferMode: 'M',
  cashBufferAmount: 'b',
  cashBufferSpreadStartAge: 'x',
  cashBufferSpreadEndAge: 'z',
};

const SHORT_TO_LONG = Object.fromEntries(
  Object.entries(LONG_TO_SHORT).map(([longKey, shortKey]) => [shortKey, longKey]),
);

const LEGACY_ALIASES = {
  annuityPurchaseAmount: 'annuityPurchaseAmountExact',
};

export function defaultCashBufferSpreadEnd(retirementAge, lifeExpectancy, currentAge = 0) {
  const end = Math.min(
    retirementAge + CASH_BUFFER_DEFAULT_SPREAD_YEARS - 1,
    lifeExpectancy,
  );
  return Math.max(currentAge, end);
}

export function defaultCashBufferSpreadStart(retirementAge) {
  return retirementAge;
}

function getDefaultForKey(key, canonical) {
  if (key === 'cashBufferSpreadStartAge') {
    return defaultCashBufferSpreadStart(canonical.retirementAge ?? SHARE_DEFAULTS.retirementAge);
  }
  if (key === 'cashBufferSpreadEndAge') {
    return defaultCashBufferSpreadEnd(
      canonical.retirementAge ?? SHARE_DEFAULTS.retirementAge,
      canonical.lifeExpectancy ?? SHARE_DEFAULTS.lifeExpectancy,
      canonical.currentAge ?? SHARE_DEFAULTS.currentAge,
    );
  }
  return SHARE_DEFAULTS[key];
}

function valuesEqual(a, b) {
  return Object.is(a, b);
}

function shouldOmitField(key, value, canonical) {
  if (value === null || value === undefined) return true;

  if (!canonical.annuityEnabled && key !== 'annuityEnabled') {
    if (
      key === 'annuityPurchasePercent'
      || key === 'annuityPurchaseAmountExact'
      || key === 'annuityIncomeRatePercent'
      || key === 'annuityAnnualGrossExact'
    ) {
      return true;
    }
  }

  if (!canonical.dbPensionEnabled && key !== 'dbPensionEnabled') {
    if (key === 'dbPensionAnnual' || key === 'dbPensionAge') {
      return true;
    }
  }

  if (!canonical.cashBufferEnabled && key !== 'cashBufferEnabled') {
    if (
      key === 'cashBufferMode'
      || key === 'cashBufferAmount'
      || key === 'cashBufferSpreadStartAge'
      || key === 'cashBufferSpreadEndAge'
    ) {
      return true;
    }
  }

  if (!canonical.includePartnerStatePension && key !== 'includePartnerStatePension') {
    if (key === 'partnerStatePensionAnnual' || key === 'partnerStatePensionAge') {
      return true;
    }
  }

  if (
    key === 'partnerStatePensionAge'
    && canonical.includePartnerStatePension
    && valuesEqual(canonical.partnerStatePensionAge, canonical.statePensionAge)
  ) {
    return true;
  }

  return valuesEqual(value, getDefaultForKey(key, canonical));
}

function encodeIncomeOverridesCompact(overrides) {
  if (!Array.isArray(overrides) || overrides.length === 0) return undefined;
  return overrides.map((row) => {
    const tuple = [row.fromAge, row.toAge, row.net];
    if (row.toAge > row.fromAge && row.inflate) tuple.push(1);
    return tuple;
  });
}

function decodeIncomeOverridesCompact(encoded) {
  if (!Array.isArray(encoded)) return [];
  return encoded
    .filter((row) => Array.isArray(row) && row.length >= 3)
    .map((row) => {
      const fromAge = Math.round(Number(row[0]));
      const toAge = Math.round(Number(row[1]));
      const net = Math.round(Number(row[2]));
      const multiYear = toAge > fromAge;
      return {
        fromAge,
        toAge,
        net,
        inflate: multiYear ? row[3] === 1 : true,
      };
    });
}

/**
 * @param {Record<string, unknown>} canonical — long-key state snapshot
 * @returns {string} base64-encoded query param value
 */
export function encodeShareParam(canonical) {
  const compact = { v: SHARE_FORMAT_V2 };

  for (const [longKey, shortKey] of Object.entries(LONG_TO_SHORT)) {
    if (!(longKey in canonical)) continue;
    const value = canonical[longKey];
    if (shouldOmitField(longKey, value, canonical)) continue;
    compact[shortKey] = value;
  }

  if (canonical.incomeOverrides?.length) {
    const encoded = encodeIncomeOverridesCompact(canonical.incomeOverrides);
    if (encoded?.length) compact.O = encoded;
  }

  return btoa(JSON.stringify(compact));
}

/**
 * @param {string} param — raw `s` query value
 * @returns {Record<string, unknown> | null} partial long-key state, or null on failure
 */
export function decodeShareParam(param) {
  if (!param) return null;

  let json;
  try {
    json = JSON.parse(atob(param));
  } catch {
    return null;
  }

  if (!json || typeof json !== 'object') return null;

  if (json.v === SHARE_FORMAT_V2) {
    const partial = {};
    for (const [shortKey, value] of Object.entries(json)) {
      if (shortKey === 'v') continue;
      const longKey = SHORT_TO_LONG[shortKey];
      if (longKey) partial[longKey] = value;
    }
    // Legacy v2 short keys y/u stored qualifying years (<= 35), not annual £
    if (partial.userStatePensionAnnual === undefined && json.y !== undefined && json.y <= 35) {
      partial.statePensionYears = json.y;
    }
    if (partial.partnerStatePensionAnnual === undefined && json.u !== undefined && json.u <= 35) {
      partial.partnerStatePensionYears = json.u;
    }
    if (json.O !== undefined) {
      partial.incomeOverrides = decodeIncomeOverridesCompact(json.O);
    }
    return partial;
  }

  const partial = { ...json };
  delete partial.v;
  delete partial.incomeMode;

  for (const [legacyKey, longKey] of Object.entries(LEGACY_ALIASES)) {
    if (legacyKey in partial && !(longKey in partial)) {
      partial[longKey] = partial[legacyKey];
      delete partial[legacyKey];
    }
  }

  return partial;
}
